/* eslint @typescript-eslint/no-explicit-any: 0 */
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import * as cognito from 'lib/cognito';

import { routes } from 'data/routes';

import { AttributesData, Permissions } from 'types';

import { usersDataService } from 'services/users.service';

export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut,
}

export interface IAuth {
  authStatus?: AuthStatus;
  getSession?: any;
  signInWithEmail?: any;
  signOut?: any;
  completeNewPasswordChallenge?: any;
  isNewPassword?: boolean;
  isNewPasswordSet?: any;
  forgotPassword?: any;
  confirmPassword?: any;
}

type AuthProps = {
  children: React.ReactNode;
};

const defaultState: IAuth = {
  authStatus: AuthStatus.Loading,
};

export const AuthContext = createContext(defaultState);

export function AuthIsSignedIn(props: AuthProps): React.ReactElement {
  const { children } = props;
  const { authStatus }: IAuth = useContext(AuthContext);

  // eslint-disable-next-line
  return <>{authStatus === AuthStatus.SignedIn ? children : null}</>;
}

export function AuthIsNotSignedIn(props: AuthProps): React.ReactElement {
  const { children } = props;
  const { authStatus }: IAuth = useContext(AuthContext);

  // eslint-disable-next-line
  return <>{authStatus === AuthStatus.SignedOut ? children : null}</>;
}

export const AuthProvider: React.FunctionComponent<AuthProps> = ({ children }) => {
  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
  const [isNewPassword, isNewPasswordSet] = useState(false);

  const navigate = useNavigate();

  async function signInWithEmail(username: string, password: string) {
    try {
      await cognito.signInWithEmail(username, password, navigate, async () => {
        const res = await usersDataService.getCurrentUser();
        if (
          res.data.permissions.includes(Permissions.ADMIN) ||
          res.data.permissions.includes(Permissions.CUSTOMER_MAPPER)
        ) {
          setAuthStatus(AuthStatus.SignedIn);
        } else {
          toast.error(
            'You don’t have proper permission to proceed, please ask your administrator'
          );
        }
      });
    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut);
      throw err;
    }
  }

  async function getSession() {
    const session = await cognito.getSession();

    return session;
  }

  async function completeNewPasswordChallenge(password: string, attributes: AttributesData) {
    cognito
      .completeNewPasswordChallenge(password, attributes)
      .then(() => {
        isNewPasswordSet(true);
        navigate(routes.login);
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  async function forgotPassword(email: string, callback: () => void) {
    await cognito.forgotPassword(email, callback);
  }

  function confirmPassword(email: string, code: string, password: string) {
    return cognito.confirmPassword(email, code, password);
  }

  function signOut() {
    cognito.signOut();
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('refreshToken');

    setAuthStatus(AuthStatus.SignedOut);
  }

  const state: IAuth = useMemo(
    () => ({
      getSession,
      authStatus,
      signInWithEmail,
      signOut,
      completeNewPasswordChallenge,
      isNewPassword,
      isNewPasswordSet,
      forgotPassword,
      confirmPassword,
    }),
    [authStatus, isNewPassword]
  );

  useEffect(() => {
    async function checkSessionInfo() {
      try {
        const session: any = await getSession();

        window.localStorage.setItem('accessToken', `${session.accessToken.jwtToken}`);
        window.localStorage.setItem('refreshToken', `${session.refreshToken.token}`);
        window.localStorage.setItem('idToken', `${session.idToken.jwtToken}`);

        setAuthStatus(AuthStatus.SignedIn);
      } catch (err) {
        setAuthStatus(AuthStatus.SignedOut);
      }
    }

    const sessionInterval = setInterval(checkSessionInfo, 1700000);

    checkSessionInfo();

    return () => clearInterval(sessionInterval);
  });

  if (authStatus === AuthStatus.Loading) {
    return null;
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};
